import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { nanoid } from '@reduxjs/toolkit';
import { LoanVariablesDialog } from 'components/Modals/LoanVariablesDialog/LoanVariablesDialog';
import { useDispatch, useSelector } from 'react-redux';
import { getLoanVariablesDialogState } from 'store/selectors/loanVariablesDialog.selector';
import { useFormContext } from 'react-hook-form';
import {
  IRACAffordabilityEngineForm,
  TRADE_IN_OVERRIDDEN,
} from 'modules/RACAffordabilityEngine/types/RACAffordabilityEngineForm';
import { getCreatedUserVehicle, getAddedIsUserVehicle, getVehicleState } from 'store/selectors/vehicle.selector';
import { costFormatter, getSubmitVehicleObjectQuery } from 'modules/core/utils';
import { logToDatadog } from 'utils/datadog';
import { DatadogEvents } from 'utils/datadogEvents';
import { RACAffordabilityEngineService } from 'modules/RACAffordabilityEngine/RACAffordabilityEngine.service';
import { LENDER_LOAN_VARIABLES_FORM_NAMES_MAPPER } from 'modules/core/constants/mappers/submitVehicles.mapper';
import { getFeaturesList, getVendorName } from 'store/selectors/ui.selector';
import { AppDispatch } from 'store/store';
import { DealTransactionType, RequestStatus, VehicleType } from 'modules/core/enums';
import {
  getLenderLoanTermsListThunk,
  getLenderLoanVariablesInfoThunk,
  getLenderVehicleMaxMileageThunk,
} from 'actions/vehicleActions';
import {
  setIsFetchingFailed,
  setIsLoading,
  setLoanTermsList,
  setLoanVariables,
} from 'store/reducers/loanVariablesDialog.reducer';
import {
  getCurrentLenderControls,
  getLenderState,
  getSortedVehicles,
  getVehicleFetchingFlags,
} from 'store/selectors/lenders.selector';
import { getUser } from 'store/selectors/auth.selector';
import { getLoanVariablesCurrentVehicle } from 'store/selectors/RACAffordabilityEngine.selector';
import { useRequestBalloonVehicleInfo } from 'components/Modals/LoanVariablesDialog/balloonPayment/hooks/useRequestBalloonVehicleInfo';
import { resetDealSourceDataFlags } from 'store/reducers/lenders.reducer';
import {
  AVAILABLE_DEAL_STRUCTURE_STATUSES,
  EMPTY_LTV_CATEGORY,
} from 'components/Modals/LoanVariablesDialog/consts/consts';
import { IAvailableDealStructureStatus } from 'components/Modals/LoanVariablesDialog/types';
import { useIsEligibleForAfg } from 'components/Modals/LoanVariablesDialog/balloonPayment/hooks/useIsEligibleForAfg';
import { checkAfgEligibility } from 'components/Modals/LoanVariablesDialog/balloonPayment/utils/afgEligibility.utils';
import { getVehicleYearFromDescription } from 'components/Modals/LoanVariablesDialog/balloonPayment/utils/vehicleDescription.utils';
import { useFormOverrideApr } from 'hooks/useFormOverrideApr';
import { useFormOverrideLtv } from 'hooks/useFormOverrideLtv';
import { SSO_TYPE } from 'modules/core/constants/ssoType.const';
import { useArraFinanceBackAdds } from 'modules/core/hooks/useArraFinanceBackAdds';
import { useIsAllyLender } from 'modules/core/hooks/useIsAllyLender';
import { useIsMtbLender } from 'modules/core/hooks/useIsMtbLender';
import { EMPTY_STRING, isRouteOneLender, ROUTE_ONE_LENDER_ID } from 'modules/core/constants';

export interface ILenderLoanVariablesDialog {
  isLoanVariablesDialogActive: boolean;
  isDealSourceCar?: boolean;
  resetState: () => void;
  onHide: () => void;
  isMakeItWork: boolean;
  isSubmitR1DealBlockedByControls?: boolean;
  isSalespersonLimitedView?: boolean;
  isRouteOneFlowForSbbOrSbc?: boolean;
  isDealerSsoSbbOrSbcFlow?: boolean;
  isShopByCallbackMode?: boolean;
  isRouteOneFlow?: boolean;
}

export const LenderLoanVariablesDialog: FC<ILenderLoanVariablesDialog> = ({
  isLoanVariablesDialogActive,
  isDealSourceCar,
  resetState,
  onHide,
  isMakeItWork,
  isSubmitR1DealBlockedByControls,
  isSalespersonLimitedView = false,
  isRouteOneFlowForSbbOrSbc = false,
  isDealerSsoSbbOrSbcFlow = false,
  isShopByCallbackMode = false,
  isRouteOneFlow = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const lenderState = useSelector(getLenderState);
  const dealSourceDataFlags = lenderState.dealSourceDataFlags;
  const user = useSelector(getUser);
  const currentVehicleData = useSelector(getLoanVariablesCurrentVehicle);
  const { loanVariables } = useSelector(getLoanVariablesDialogState);
  const vendorName = useSelector(getVendorName);
  const createdUserVehicle = useSelector(getCreatedUserVehicle);
  const { is_calculated_book_value } = useSelector(getFeaturesList);
  const isAddedUserVehicle = useSelector(getAddedIsUserVehicle);
  const { balloonMaxOdometers } = useSelector(getVehicleState);
  const { dealSourceBackAdds } = useArraFinanceBackAdds();
  const { isVscTaxable, isGapTaxable } = useSelector(getCurrentLenderControls);
  const taxExemptFields = useMemo(
    () => ['amountOwnedOnTrade', ...(isVscTaxable ? [] : ['vsc']), ...(isGapTaxable ? [] : ['gap'])],
    [isVscTaxable, isGapTaxable],
  );
  const isAllyLender = useIsAllyLender(true);
  const isMtbLender = useIsMtbLender(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const modalId = useMemo(() => nanoid(), [currentVehicleData?.id]);

  const { getValues: commonGetValues } = useFormContext<IRACAffordabilityEngineForm>();

  const [formHistory, setFormHistory] = useState<any[]>([]);
  const [currentOdometer, setCurrentOdometer] = useState<number | null | undefined>(null);

  const handleOdometerChange = useCallback((odometer: number | null | undefined) => {
    setCurrentOdometer(odometer);
  }, []);

  const { grossMonthlyIncome, desiredMonthlyPayment, feesAndTaxes, tier, shopByCallback } = commonGetValues() as any;

  const dealSourceData = lenderState.dealSourceData;
  const isWholesaleInventory = user?.withWholesaleInventory;
  const r1BookValue = dealSourceData?.sale_vehicle?.book_value;
  const isLtvCategoriesExists = loanVariables?.ltv_ratio;
  const isDealSourceVehicle =
    ((loanVariables?.id || currentVehicleData?.id) &&
      (loanVariables?.id || currentVehicleData?.id) === dealSourceData?.vehicle_id) ||
    isDealSourceCar;
  const manuallyCreated =
    currentVehicleData?.manually_created ||
    (isDealSourceVehicle && dealSourceData?.manually_created) ||
    isAddedUserVehicle;
  const isHideGuidebookValue =
    (is_calculated_book_value &&
      !isDealSourceVehicle &&
      !manuallyCreated &&
      currentVehicleData?.condition?.toLowerCase() === VehicleType.USED) ||
    (isWholesaleInventory &&
      !(
        isMakeItWork &&
        typeof r1BookValue === 'number' &&
        Number.isFinite(r1BookValue) &&
        r1BookValue > 0 &&
        isDealSourceVehicle
      ));
  const shouldRenderRouteOneUserId: boolean = Boolean(
    ((!user?.is_sso && lenderState?.route_one_app && lenderState?.route_one_id) ||
      !!user?.isAlternativeSso ||
      (!user?.is_sso && isRouteOneFlowForSbbOrSbc)) &&
      isMakeItWork &&
      !isSubmitR1DealBlockedByControls &&
      (dealSourceDataFlags?.message.length === 0 ||
        (dealSourceDataFlags?.message.length > 0 && dealSourceDataFlags.isInvalid)),
  );

  const isBalloonPaymentAvailable =
    lenderState.afg &&
    !!lenderState.afg_id &&
    parseFloat(tier.afg_rate) >= 0 &&
    (!isSalespersonLimitedView || lenderState.isBalloonSalespersonAccess);

  const vehicleYear = isDealSourceVehicle
    ? dealSourceData?.sale_vehicle?.year ?? getVehicleYearFromDescription(loanVariables?.description)
    : getVehicleYearFromDescription(loanVariables?.description);
  const effectiveOdometer =
    currentOdometer ??
    (isDealSourceVehicle ? dealSourceData?.sale_vehicle?.mileage : undefined) ??
    loanVariables?.odometer;
  const isEligibleForAfg = useIsEligibleForAfg({
    odometer: effectiveOdometer,
    vehicleYear,
  });

  const isDealerSso = user?.is_sso && user?.sso_type === SSO_TYPE.DEALER;

  const getBalloonToggleForVehicle = useCallback(
    (odometer: number | null | undefined, year: string | number | null | undefined) =>
      isBalloonPaymentAvailable &&
      checkAfgEligibility({ balloonMaxOdometers, odometer, vehicleYear: year }) &&
      (dealSourceData.transaction_type === DealTransactionType.BALLOON_PAYMENT ||
        (!!isDealerSso && !isDealerSsoSbbOrSbcFlow)),
    [
      isBalloonPaymentAvailable,
      balloonMaxOdometers,
      dealSourceData.transaction_type,
      isDealerSso,
      isDealerSsoSbbOrSbcFlow,
    ],
  );

  const defaultBalloonToggle = getBalloonToggleForVehicle(effectiveOdometer, vehicleYear);

  const isReadyToFetchLoanData = !isBalloonPaymentAvailable || !!lenderState.balloonInitDataLoaded;

  useRequestBalloonVehicleInfo(
    isBalloonPaymentAvailable,
    isEligibleForAfg,
    loanVariables,
    modalId,
    dealSourceData,
    lenderState?.route_one_id,
  );

  const getTargetPayment = useCallback(() => {
    if (dealSourceData?.finance_details?.vsc_payment_allowance) {
      return desiredMonthlyPayment + dealSourceData.finance_details.vsc_payment_allowance;
    }

    return desiredMonthlyPayment;
  }, [desiredMonthlyPayment, dealSourceData?.finance_details?.vsc_payment_allowance]);

  const getCurrentPayment = useCallback(
    () => (loanVariables?.calc_monthly_payment ?? loanVariables?.monthly_payment) || NaN,
    [loanVariables?.calc_monthly_payment, loanVariables?.monthly_payment],
  );

  const getTotalAmountFinanced = useCallback(() => loanVariables?.total_financed || NaN, [
    loanVariables?.total_financed,
  ]);

  const { hasFormOverrideApr, getFormOverrideApr, getOverrideApr } = useFormOverrideApr(commonGetValues);
  const { getLtvHighLimit } = useFormOverrideLtv(commonGetValues);

  const getOverrideAprValue = useCallback(
    () =>
      getOverrideApr({
        balloonToggle: defaultBalloonToggle,
        isShopByCallbackMode,
        dealSourceOverrideApr: dealSourceData?.finance_details?.override_apr,
        dealSourceTransactionType: dealSourceData?.transaction_type,
      }),
    [
      defaultBalloonToggle,
      isShopByCallbackMode,
      dealSourceData?.finance_details?.override_apr,
      dealSourceData?.transaction_type,
      getOverrideApr,
    ],
  );

  const getAprValue = useCallback(() => {
    if (isShopByCallbackMode) {
      return (commonGetValues() as any).shopByCallback?.sbcApr;
    }

    if (
      hasFormOverrideApr() &&
      !defaultBalloonToggle &&
      dealSourceData?.transaction_type !== DealTransactionType.BALLOON_PAYMENT
    ) {
      return getFormOverrideApr();
    }

    if (
      !!dealSourceData?.finance_details?.override_apr &&
      dealSourceData?.finance_details?.apr &&
      !defaultBalloonToggle &&
      dealSourceData?.transaction_type !== DealTransactionType.BALLOON_PAYMENT
    ) {
      return dealSourceData.finance_details.apr;
    }

    if (
      isDealSourceVehicle &&
      dealSourceData?.finance_details?.apr &&
      !defaultBalloonToggle &&
      dealSourceData?.transaction_type !== DealTransactionType.BALLOON_PAYMENT
    ) {
      return dealSourceData.finance_details.apr;
    }

    return undefined;
  }, [
    isDealSourceVehicle,
    defaultBalloonToggle,
    dealSourceData?.finance_details,
    dealSourceData?.transaction_type,
    isShopByCallbackMode,
    hasFormOverrideApr,
    getFormOverrideApr,
    commonGetValues,
  ]);

  const getLoanVariablesData = useCallback(
    async (defaultValues) => {
      const values = commonGetValues();
      const { totalNetDownPaymentDialog, cashDownOverridden, [TRADE_IN_OVERRIDDEN]: tradeInOverridden } = values as any;
      const dealSourceCashDownPayment =
        (dealSourceData?.finance_details?.cash_down || 0) + (dealSourceData?.finance_details?.rebates || 0);
      const cashDownPayment = cashDownOverridden ? totalNetDownPaymentDialog?.cashDown || 0 : dealSourceCashDownPayment;
      const tradeValue = tradeInOverridden
        ? totalNetDownPaymentDialog?.tradeValue || 0
        : dealSourceData?.trade_in_vehicle?.value;
      const amountOwed = tradeInOverridden
        ? totalNetDownPaymentDialog?.amountOwed || 0
        : dealSourceData?.trade_in_vehicle?.amount_owed;

      const vehicleParameters = getSubmitVehicleObjectQuery<IRACAffordabilityEngineForm>(
        { ...currentVehicleData, ...values, vendorName: vendorName, isLender: true },
        RACAffordabilityEngineService.getSubmitQueryForLoanVariables,
        LENDER_LOAN_VARIABLES_FORM_NAMES_MAPPER,
      );

      const vehicleYear = isAddedUserVehicle ? createdUserVehicle?.year : currentVehicleData?.year;
      const vehicleMileage = isAddedUserVehicle ? createdUserVehicle?.odometer : currentVehicleData?.odometer;
      const r1DealerId =
        (user?.isWithInventory
          ? (commonGetValues() as any).routeOneDealer?.value
          : (commonGetValues() as any).routeOneDealerId) ||
        user?.originalExternalDealerId ||
        undefined;
      const conversationId = dealSourceData?.conversation_id;
      const termParams = {
        mileage: isDealSourceVehicle ? dealSourceData?.sale_vehicle?.mileage : vehicleMileage,
        tier: (commonGetValues().tier as any).tier_name,
        year: isDealSourceVehicle ? parseInt(dealSourceData?.sale_vehicle?.year) : vehicleYear,
        lender_name: commonGetValues().lenderName,
        balloon_payment: defaultBalloonToggle,
        ...(r1DealerId && { r1_dealer_id: r1DealerId }),
        ...(conversationId && { conversation_id: conversationId }),
      };

      // Dealer SSO: ETG creditdecision may return term=0 for RAC Lender (F00A14).
      // Pre-fetch loan terms list to use the first available term as a fallback.
      const isDealerSso = user?.sso_type === SSO_TYPE.DEALER;
      const shouldPrefetchTermsForDealerSso =
        isDealerSso &&
        defaultBalloonToggle &&
        isDealSourceVehicle &&
        !isShopByCallbackMode &&
        !dealSourceData.finance_details?.term;
      let dealerSsoFallbackLoanTerm: number | undefined;

      if (shouldPrefetchTermsForDealerSso) {
        const prefetchedTerms: any = await dispatch(getLenderLoanTermsListThunk(termParams));

        dealerSsoFallbackLoanTerm = prefetchedTerms?.payload?.[0];
      }

      const allyGapOverride =
        isAllyLender || isMtbLender ? { gap: 0, vsc_and_gap_insurance: vehicleParameters.vsc || 0 } : {};

      const requestedDealBookValue = dealSourceData?.sale_vehicle?.book_value;
      const shouldRequestDealBookValue =
        (isAllyLender || isMtbLender) &&
        typeof requestedDealBookValue === 'number' &&
        Number.isFinite(requestedDealBookValue) &&
        requestedDealBookValue > 0;

      const params = {
        ...vehicleParameters,
        vehicle_id: currentVehicleData?.id,
        guidebook_value: undefined,
        sales_fees: feesAndTaxes.estimatedFees || 0,
        estimated_fees: undefined,
        year: currentVehicleData?.year,
        ...(isDealSourceVehicle && {
          ...(dealSourceData.taxes_fees?.taxes && { taxes: dealSourceData.taxes_fees.taxes || 0 }),
          vehicle_id: dealSourceData.vehicle_id,
          guidebook_value: shouldRequestDealBookValue ? requestedDealBookValue : undefined,
          taxes: dealSourceData.taxes_fees?.taxes || 0,
          add_ons: 0,
          amount_owed: amountOwed,
          cash_down_payment: cashDownPayment,
          current_price: dealSourceData.sale_vehicle.retail_price,
          loan_term: isShopByCallbackMode
            ? vehicleParameters.loan_term
            : dealSourceData.finance_details.term || dealerSsoFallbackLoanTerm || 0,
          net_down_payment: (tradeValue || 0) - (amountOwed || 0),
          ...(dealSourceBackAdds !== undefined && { vsc: dealSourceBackAdds }),
          odometer: dealSourceData.sale_vehicle.mileage,
          trade_value: tradeValue,
          sales_tax_rate: feesAndTaxes.salesTaxRate,
          year: parseInt(dealSourceData.sale_vehicle.year),
        }),
        ...(isAddedUserVehicle && {
          vehicle_id: createdUserVehicle.id,
          guidebook_value: undefined,
          current_price: createdUserVehicle.current_price,
          odometer: createdUserVehicle.odometer,
          loan_term: isShopByCallbackMode ? shopByCallback.sbcTerm.value : createdUserVehicle.loan_term,
          year: createdUserVehicle.year,
          taxes: undefined,
        }),
        apr: getAprValue(),
        override_apr: getOverrideAprValue(),
        apply_cpo: Boolean(defaultValues.loanVariablesForm.applyCpo),
        override_vsc: dealSourceData?.finance_details?.override_vsc,
        total_ltv_limit: dealSourceData?.finance_details?.total_ltv_limit,
        bank_discount: feesAndTaxes.bankDiscount,
        bank_fees: feesAndTaxes.bankFees,
        lender_name: commonGetValues().lenderName,
        is_ltv: commonGetValues().preferences.ltvTaxesFeesFilter,
        deal_source_ltv_high_limit: dealSourceData?.finance_details?.high_ltv_limit,
        ltv_high_limit: getLtvHighLimit({
          isShopByCallbackMode,
          sbcLtv: (values as any).shopByCallback?.sbcLtv,
          tierLtvLimit: (values.tier as any)?.ltv_limit,
          dealSourceLtv: dealSourceData?.finance_details?.high_ltv_limit,
        }),
        monthly_income: grossMonthlyIncome,
        targeted_payment: getTargetPayment(),
        approved_monthly_payment: getTargetPayment(),
        include_calc_monthly_payment: isShopByCallbackMode,
        include_eligible_terms: !defaultBalloonToggle,
        is_override_apr_entered: hasFormOverrideApr(),
        ...(r1DealerId && { r1_dealer_id: r1DealerId }),
        ...allyGapOverride,
      };

      const loanVariablesInfoResponse = await dispatch(getLenderLoanVariablesInfoThunk(params));

      await dispatch(
        getLenderVehicleMaxMileageThunk({
          modalId: modalId,
          params: {
            lender_name: termParams.lender_name,
            tier: termParams.tier,
            year: termParams.year,
          },
        }),
      );
      const loanVariablesInfoPayload: any = loanVariablesInfoResponse.payload;
      const fetchedOdometer = currentOdometer ?? loanVariablesInfoPayload?.odometer;
      const fetchedVehicleYear = getVehicleYearFromDescription(loanVariablesInfoPayload?.description);
      const actualBalloonToggle = isDealSourceVehicle
        ? defaultBalloonToggle
        : getBalloonToggleForVehicle(fetchedOdometer, fetchedVehicleYear);

      const hasValidatedLoanTerms =
        !actualBalloonToggle && Array.isArray(loanVariablesInfoPayload?.eligible_loan_terms);
      let loanTermsListResponse: any;

      if (!hasValidatedLoanTerms) {
        loanTermsListResponse = await dispatch(
          getLenderLoanTermsListThunk({ ...termParams, balloon_payment: actualBalloonToggle }),
        );
      }

      const actualLoanTermsList = hasValidatedLoanTerms
        ? loanVariablesInfoPayload.eligible_loan_terms
        : loanTermsListResponse?.payload ?? [];

      const isLoanVariablesRejected = loanVariablesInfoResponse?.meta?.requestStatus === RequestStatus.REJECTED;
      const isLoanTermsRejected = loanTermsListResponse?.meta?.requestStatus === RequestStatus.REJECTED;

      if (isLoanVariablesRejected || isLoanTermsRejected) {
        const error = isLoanVariablesRejected
          ? (loanVariablesInfoResponse as any)?.payload
          : (loanTermsListResponse as any)?.payload;

        logToDatadog(
          DatadogEvents.LOG_CATEGORIES[DatadogEvents.FAILED_TO_RETRIEVE_DATA],
          {
            log_category: DatadogEvents.FAILED_TO_RETRIEVE_DATA,
            vehicle: currentVehicleData,
            error_details: {
              message: error?.message,
              status: error?.response?.status,
              statusText: error?.response?.statusText,
              data: error?.response?.data,
            },
          },
          user,
        );
        dispatch(setIsFetchingFailed({ modalId: modalId, isFetchingFailed: true }));
      }

      dispatch(setLoanTermsList({ modalId: modalId, loanTermsList: actualLoanTermsList }));
      dispatch(setLoanVariables({ modalId: modalId, loanVariables: loanVariablesInfoResponse.payload }));

      const loanVariablesInfo: any = loanVariablesInfoResponse.payload;
      const selectedLoanTerm = actualLoanTermsList.includes(loanVariablesInfo?.term)
        ? loanVariablesInfo.term
        : actualLoanTermsList[0] ?? EMPTY_STRING;
      const dealBookValue = dealSourceData?.sale_vehicle?.book_value;
      const shouldUseDealBookValue =
        typeof dealBookValue === 'number' && Number.isFinite(dealBookValue) && dealBookValue > 0;
      let guidebookValueForHistory = defaultValues.loanVariablesForm.guidebookValue;

      if (isAddedUserVehicle) {
        guidebookValueForHistory = createdUserVehicle?.book_value ?? guidebookValueForHistory;
      } else if (isDealSourceVehicle) {
        guidebookValueForHistory =
          (shouldUseDealBookValue ? dealBookValue : loanVariablesInfo?.guidebook_value) ?? guidebookValueForHistory;
      } else if (!isHideGuidebookValue) {
        guidebookValueForHistory = loanVariablesInfo?.guidebook_value ?? guidebookValueForHistory;
      }

      setFormHistory([
        {
          loanVariablesForm: {
            ...defaultValues.loanVariablesForm,
            balloonToggle: actualBalloonToggle,
            loanTerm: {
              title: selectedLoanTerm,
              value: selectedLoanTerm,
            },
            loanTermsList: actualLoanTermsList,
            apr: loanVariablesInfo?.customer_rate || NaN,
            totalAmountFinanced: loanVariablesInfo?.total_financed || NaN,
            taxes: loanVariablesInfo?.taxes || 0,
            ltvCategories: loanVariablesInfo,
            currentPayment: (loanVariablesInfo?.calc_monthly_payment ?? loanVariablesInfo?.monthly_payment) || [],
            guidebookValue: guidebookValueForHistory,
            confirmBook: isSalespersonLimitedView || undefined,
          },
        },
      ]);

      dispatch(setIsLoading({ modalId: modalId, isLoading: false }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      commonGetValues,
      currentVehicleData,
      dealSourceBackAdds,
      isAllyLender,
      isMtbLender,
      dispatch,
      vendorName,
      isDealSourceVehicle,
      dealSourceData,
      createdUserVehicle,
      desiredMonthlyPayment,
      getOverrideAprValue,
      getLtvHighLimit,
      defaultBalloonToggle,
      getBalloonToggleForVehicle,
      currentOdometer,
    ],
  );

  const { t } = useTranslation();

  const getActualPaymentInfoMessage = useCallback(
    (loanVariables: any, availableDealStructureStatus?: IAvailableDealStructureStatus) => {
      const isAmericreditLender = isRouteOneLender(lenderState?.route_one_id, ROUTE_ONE_LENDER_ID.AMERICREDIT);
      const isMtbLender = isRouteOneLender(lenderState?.route_one_id, ROUTE_ONE_LENDER_ID.MTB);

      if (isAmericreditLender && availableDealStructureStatus === AVAILABLE_DEAL_STRUCTURE_STATUSES.OUT_OF_POLICY) {
        return t('components.loanVariablesModal.infoMessages.americreditOutOfPolicy');
      }

      const allyCPO = costFormatter(loanVariables?.ally_cpo, false);
      const allyCPOMessage =
        loanVariables?.ally_cpo && !isAmericreditLender && !isMtbLender
          ? t('components.loanVariablesModal.infoMessages.allyCPO', { allyCPO, mark: isAllyLender ? '¹' : '⁴' })
          : '';

      const isResubmitForPricing = [
        ROUTE_ONE_LENDER_ID.ALLY,
        ROUTE_ONE_LENDER_ID.AMERICREDIT,
        ROUTE_ONE_LENDER_ID.MTB,
      ].some((routeOneId) => isRouteOneLender(lenderState?.route_one_id, routeOneId));
      const messageKey = isResubmitForPricing
        ? 'components.loanVariablesModal.infoMessages.allyLenderActualPayment'
        : 'components.loanVariablesModal.infoMessages.lenderActualPayment';

      return t(messageKey, { allyCPOMessage: allyCPOMessage });
    },
    [t, lenderState?.route_one_id, isAllyLender],
  );

  const getLtvCategories = useCallback(
    (loanVariables: any, availableDealStructureStatus: IAvailableDealStructureStatus) => {
      if (availableDealStructureStatus === AVAILABLE_DEAL_STRUCTURE_STATUSES.MISSING_OR_INVALID_INFO) {
        return new Array(1).fill(EMPTY_LTV_CATEGORY);
      } else {
        return [loanVariables, ...(loanVariables?.flex_ltv_opportunities ?? [])];
      }
    },
    [],
  );

  const transformedLoanVariables = useMemo(() => {
    if (manuallyCreated && loanVariables) {
      return { ...loanVariables, profit: null };
    }

    return loanVariables;
  }, [loanVariables, manuallyCreated]);

  const apr = loanVariables?.customer_rate ?? getAprValue();

  return (
    <LoanVariablesDialog
      modalId={modalId}
      dealSourceData={lenderState.dealSourceData}
      dealSourceDataFlags={dealSourceDataFlags}
      resetDealSourceDataFlags={resetDealSourceDataFlags}
      headerLogoSrc={lenderState.logo}
      isLender={true}
      taxExemptFields={taxExemptFields}
      isLoanVariablesDialogActive={isLoanVariablesDialogActive}
      resetState={resetState}
      onHide={onHide}
      isDealSourceCar={isDealSourceCar}
      manuallyCreated={manuallyCreated}
      isAddedUserVehicle={isAddedUserVehicle}
      isMakeItWork={isMakeItWork}
      isSubmitR1DealBlockedByControls={isSubmitR1DealBlockedByControls}
      getLoanVariablesData={getLoanVariablesData}
      formHistory={formHistory}
      setFormHistory={setFormHistory}
      loanTermValue={loanVariables?.term}
      defaultLoanTermValue={isShopByCallbackMode ? loanVariables?.term : undefined}
      routeOneUserId={user?.routeOneUserId}
      ltvCategories={isLtvCategoriesExists ? loanVariables : []}
      apr={apr}
      guidebookValue={isHideGuidebookValue ? 0 : loanVariables?.guidebook_value || 0}
      getTargetPayment={getTargetPayment}
      getTotalAmountFinanced={getTotalAmountFinanced}
      getCurrentPayment={getCurrentPayment}
      isBalloonPaymentAvailable={isBalloonPaymentAvailable}
      isEligibleForAfg={isEligibleForAfg}
      defaultBalloonToggle={defaultBalloonToggle}
      isReadyToFetchLoanData={isReadyToFetchLoanData}
      sortedVehiclesSelector={getSortedVehicles}
      vehiclesFetchingFlagsSelector={getVehicleFetchingFlags}
      isSalespersonLimitedView={isSalespersonLimitedView}
      shouldRenderRouteOneUserId={shouldRenderRouteOneUserId}
      getActualPaymentInfoMessage={getActualPaymentInfoMessage}
      getLtvCategories={getLtvCategories}
      transformedLoanVariables={transformedLoanVariables}
      isShopByCallbackMode={isShopByCallbackMode}
      isRouteOneFlow={isRouteOneFlow}
      isRouteOneFlowForSbbOrSbc={isRouteOneFlowForSbbOrSbc}
      isDealerSsoSbbOrSbcFlow={isDealerSsoSbbOrSbcFlow}
      isDisplayPolicyStatusMessage={false}
      onOdometerChange={handleOdometerChange}
    />
  );
};
